<script>
  import { createEventDispatcher, onMount } from "svelte";
  import moment from "moment";
  import { normalizeCampaignRow, getStatusInfo } from "../../../../utils/freebetsCore.js";
  import { CAMPAIGN_STATUS, computeCampaignStatus } from "../../../../utils/statusEngine.js";
  import { loadAnakatechClients } from "./utils/anakatechCatalogs.js";

  moment.locale("es");

  export let api = {
    listCampaigns: async () => {
      throw new Error("api.listCampaigns no inyectado");
    },
  };
  export let filters = {
    client: "",
    campaignName: "",
    statuses: [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.SCHEDULED],
  };
  export let icons = {};
  export let timeFormat = "DD/MM, hh:mmA";

  const dispatch = createEventDispatcher();
  const pageSizeOptions = [10, 50, 100, 200];

  let rows = [];
  let isLoading = false;
  let errorMessage = "";
  let page = 1;
  let pageSize = 10;
  let sortKey = "#";
  let sortAsc = false;
  let mounted = false;
  let loadSequence = 0;
  let clientNameByCode = new Map();

  const norm = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();

  function clientCode(row) {
    return String(row?.client?.clientCode ?? row?.clientCodeStr ?? "").trim();
  }

  function clientName(row) {
    const code = clientCode(row);
    return (
      clientNameByCode.get(code) ||
      row?.client?.clientName ||
      row?.clientLabel ||
      code ||
      "-"
    );
  }

  function mapCampaign(document) {
    const code = String(document?.client?.clientCode ?? "").trim();
    const enriched = {
      ...document,
      client: {
        ...document?.client,
        clientName:
          clientNameByCode.get(code) ||
          document?.client?.clientName ||
          code ||
          "-",
      },
    };
    const base = normalizeCampaignRow(enriched);
    const startMs = base.startDate ? new Date(base.startDate).getTime() : 0;
    const endMs = base.endDate ? new Date(base.endDate).getTime() : 0;
    const statusCode = computeCampaignStatus(base.statusCode, startMs, endMs);
    const statusInfo = getStatusInfo(statusCode);
    return {
      ...base,
      rowId: document?.id || document?.campaignId,
      displayName: document?.campaignName || document?.campaignId || "-",
      campaignCode: document?.campaignId || "-",
      playerCount: Array.isArray(document?.playerList)
        ? document.playerList.length
        : 0,
      statusCode,
      statusInfoClass: statusInfo.class,
      statusInfoLabel: statusInfo.label,
    };
  }

  export async function refresh() {
    const sequence = ++loadSequence;
    isLoading = true;
    errorMessage = "";

    try {
      const { clients, allowedCodes } = await loadAnakatechClients();
      if (Array.isArray(allowedCodes) && !allowedCodes.length) {
        rows = [];
        return;
      }

      clientNameByCode = new Map(
        clients
          .map((client) => [
            String(client?.code ?? "").trim(),
            String(
              client?.namesite ??
                client?.clientName ??
                client?.name ??
                client?.code ??
                "",
            ).trim(),
          ])
          .filter(([code]) => code),
      );

      const params = {};
      if (filters.client) {
        params.clientCodes = filters.client;
      } else if (Array.isArray(allowedCodes) && allowedCodes.length) {
        params.clientCodes = allowedCodes.join(",");
      }

      const response = await api.listCampaigns("ank", params);
      if (sequence !== loadSequence) return;

      const raw =
        response?.data?.list ??
        response?.data?.data ??
        response?.data ??
        response ??
        [];
      rows = (Array.isArray(raw) ? raw : [])
        .filter(
          (campaign) =>
            String(campaign?.campaignType ?? "").toUpperCase() === "SLOT",
        )
        .map(mapCampaign);
    } catch (error) {
      if (sequence !== loadSequence) return;
      console.error("Error cargando campanas Free Spins de Anakatech:", error);
      rows = [];
      errorMessage = "No se pudo cargar la lista de Free Spins.";
    } finally {
      if (sequence === loadSequence) isLoading = false;
    }
  }

  function setSort(key, preferredAsc = null) {
    if (sortKey === key) {
      sortAsc = !sortAsc;
    } else {
      sortKey = key;
      sortAsc = preferredAsc ?? true;
    }
  }

  function sortValue(row, key) {
    switch (key) {
      case "client":
        return norm(clientName(row));
      case "campaign":
        return norm(row.campaignName);
      case "game":
        return norm(row.gameLabel);
      case "currency":
        return norm(row.currencyLabel);
      case "players":
        return Number(row.playerCount);
      case "start":
        return row.startDate ? new Date(row.startDate).getTime() : 0;
      case "end":
        return row.endDate ? new Date(row.endDate).getTime() : 0;
      case "status":
        return Number(row.statusCode);
      default:
        return 0;
    }
  }

  function sortRows(source) {
    const sorted = [...source];
    if (sortKey === "#") {
      if (!sortAsc) sorted.reverse();
      return sorted;
    }
    return sorted.sort((left, right) => {
      const a = sortValue(left, sortKey);
      const b = sortValue(right, sortKey);
      const result =
        typeof a === "string" ? a.localeCompare(b) : Number(a) - Number(b);
      return sortAsc ? result : -result;
    });
  }

  function canModify(campaign) {
    return [CAMPAIGN_STATUS.ACTIVE, CAMPAIGN_STATUS.SCHEDULED].includes(
      Number(campaign?.statusCode),
    );
  }

  $: selectedStatuses = Array.isArray(filters?.statuses)
    ? filters.statuses.map(Number).filter(Number.isFinite)
    : [];
  $: filteredRows = sortRows(
    rows.filter((row) => {
      const selectedClient = norm(filters?.client);
      const selectedName = norm(filters?.campaignName);
      const matchesClient =
        !selectedClient ||
        norm(clientCode(row)) === selectedClient ||
        norm(clientName(row)).includes(selectedClient);
      const matchesCampaign =
        !selectedName ||
        norm(row.campaignName).includes(selectedName) ||
        norm(row.campaignId).includes(selectedName);
      const matchesStatus =
        !selectedStatuses.length ||
        selectedStatuses.includes(Number(row.statusCode));
      return matchesClient && matchesCampaign && matchesStatus;
    }),
  );
  $: totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  $: if (page > totalPages) page = totalPages;
  $: pageStart = (page - 1) * pageSize;
  $: visibleRows = filteredRows.slice(pageStart, pageStart + pageSize);
  $: filterToken = JSON.stringify({
    client: filters?.client,
    campaignName: filters?.campaignName,
    statuses: filters?.statuses,
  });
  $: if (mounted && filterToken) page = 1;

  onMount(() => {
    mounted = true;
    refresh();
  });
</script>

{#if isLoading}
  <p class="state-message">Cargando Free Spins...</p>
{:else if errorMessage}
  <p class="state-message error">{errorMessage}</p>
{:else if visibleRows.length === 0}
  <p class="state-message">No se encontraron Free Spins.</p>
{:else}
  <div class="table-wrapper">
    <table class="table table-striped table-hover table-bordered align-middle">
      <thead>
        <tr>
          <th class="th-sort" on:click={() => setSort("#")}>N&deg;</th>
          <th class="th-sort" on:click={() => setSort("client", true)}>Cliente</th>
          <th class="th-sort" on:click={() => setSort("campaign", true)}>Campa&ntilde;a</th>
          <th>Tipo</th>
          <th class="th-sort details-heading" on:click={() => setSort("game", true)}>
            Juego / Monto / Rondas
          </th>
          <th class="th-sort" on:click={() => setSort("currency", true)}>Moneda</th>
          <th class="th-sort" on:click={() => setSort("players", false)}>Jugadores</th>
          <th class="th-sort" on:click={() => setSort("start", false)}>Inicio</th>
          <th class="th-sort" on:click={() => setSort("end", false)}>Fin</th>
          <th class="th-sort" on:click={() => setSort("status", false)}>Estado</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {#each visibleRows as campaign, index (campaign.rowId || campaign.campaignId || index)}
          <tr>
            <td>{pageStart + index + 1}</td>
            <td title={clientCode(campaign)}>{clientName(campaign)}</td>
            <td>
              <div class="campaign-cell">
                <span>{campaign.campaignName || campaign.campaignId}</span>
                {#if campaign.campaignName && campaign.campaignName !== campaign.campaignId}
                  <small>{campaign.campaignId}</small>
                {/if}
              </div>
            </td>
            <td><span>Free Spins</span></td>
            <td>
              <div class="config-list">
                {#each campaign.gameConfigs || [] as config}
                  <div class="config-card">
                    <span title={config.game}>{config.game}</span>
                    <small>Monto: {config.amount} / Rondas: {config.rounds}</small>
                  </div>
                {:else}
                  <span>-</span>
                {/each}
              </div>
            </td>
            <td>{campaign.currencyLabel}</td>
            <td>{campaign.playerCount}</td>
            <td title={campaign.startDate ? moment(campaign.startDate).format("DD/MM/YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.startDate ? moment(campaign.startDate).format(timeFormat) : "-"}</span>
            </td>
            <td title={campaign.endDate ? moment(campaign.endDate).format("DD/MM/YYYY, hh:mm A") : ""}>
              <span class="date-cell">{campaign.endDate ? moment(campaign.endDate).format(timeFormat) : "-"}</span>
            </td>
            <td>
              <span class={`badge status-badge rounded-pill ${campaign.statusInfoClass}`}>
                <span class="status-dot"></span>
                {campaign.statusInfoLabel}
              </span>
            </td>
            <td class="actions-cell">
              <button
                type="button"
                class="btn-icon players-button"
                title="Ver jugadores"
                on:click={() => dispatch("addplayers", campaign)}
              >
                {@html icons.persons || "+"}
              </button>
              <button
                type="button"
                class="btn-icon delete-button"
                title="Eliminar Free Spin"
                disabled={Number(campaign.statusCode) === CAMPAIGN_STATUS.DELETED}
                on:click={() => dispatch("delete", campaign)}
              >
                {@html icons.delete || "X"}
              </button>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<div class="pagination-bar">
  <span class="total-badge">Total: <strong>{filteredRows.length}</strong></span>
  <div class="page-controls">
    <button on:click={() => (page = 1)} disabled={page === 1} aria-label="Primera pagina">&laquo;</button>
    <button on:click={() => (page = Math.max(1, page - 1))} disabled={page === 1} aria-label="Pagina anterior">&lsaquo;</button>
    <span>Pagina <strong>{page}</strong> de <strong>{totalPages}</strong></span>
    <button on:click={() => (page = Math.min(totalPages, page + 1))} disabled={page >= totalPages} aria-label="Pagina siguiente">&rsaquo;</button>
    <button on:click={() => (page = totalPages)} disabled={page >= totalPages} aria-label="Ultima pagina">&raquo;</button>
  </div>
  <select bind:value={pageSize} on:change={() => { pageSize = Number(pageSize); page = 1; }}>
    {#each pageSizeOptions as option}
      <option value={option}>{option} / pag.</option>
    {/each}
  </select>
</div>

<style>
  .state-message {
    margin: 1rem;
    color: #6c757d;
    text-align: center;
  }
  .state-message.error {
    color: #dc3545;
  }
  .table-wrapper {
    max-height: 70vh;
    margin: 0 10px;
    overflow: auto;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
  }
  .table-wrapper table {
    margin-bottom: 0;
  }
  .th-sort {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0.6rem 0.5rem;
    border-bottom: 0;
    background: #fff;
    box-shadow: 0 1px 0 #dee2e6;
    color: #555;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  tbody td {
    padding: 0.4rem 0.5rem;
    vertical-align: middle;
    font-size: 0.84rem;
  }
  .campaign-cell,
  .config-list,
  .config-card {
    display: flex;
    flex-direction: column;
  }
  .campaign-cell small,
  .config-card small {
    color: #6c757d;
  }
  .config-list {
    min-width: 190px;
    gap: 0.25rem;
  }
  .config-card {
    max-width: 260px;
    padding: 0.3rem 0.45rem;
    border: 1px solid #e8edf3;
    border-radius: 0.35rem;
    background: #f8fafc;
  }
  .config-card > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .type-badge {
    display: inline-flex;
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
    background: #eef4ff;
    color: #2457a6;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .date-cell {
    white-space: nowrap;
  }
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35em 0.75em;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
  .actions-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }
  .btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
  }
  .players-button {
    border-color: #cfe2ff;
    background: #f0f7ff;
    color: #0d6efd;
  }
  .delete-button {
    border-color: #f1aeb5;
    background: #fff5f5;
    color: #dc3545;
  }
  .players-button:hover:not(:disabled) {
    border-color: #0d6efd;
    background: #0d6efd;
    color: #fff;
  }
  .delete-button:hover:not(:disabled) {
    border-color: #dc3545;
    background: #dc3545;
    color: #fff;
  }
  .btn-icon:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.5rem 10px 0;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.5rem;
    background: #fff;
  }
  .total-badge,
  .page-controls,
  .pagination-bar select {
    color: #555;
    font-size: 0.8rem;
  }
  .page-controls {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .page-controls button {
    width: 34px;
    height: 34px;
    padding: 0;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.4rem;
    background: #fff;
  }
  .page-controls button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
  .pagination-bar select {
    padding: 0.3rem 0.55rem;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 0.4rem;
    background: #fff;
  }
  @media (max-width: 900px) {
    .details-heading {
      min-width: 210px;
    }
  }
  @media (max-width: 720px) {
    .pagination-bar {
      flex-direction: column;
    }
  }
</style>
